import {Component} from '@angular/core';

import {User} from '@models/user/user';
import { HttpResponse } from '@angular/common/http';
import {Organization} from '@models/organization/organization';
import {OrganizationConstant} from '@models/adminstration/organization/constants/organization-constant';
import {ConstantsService} from '@services/administration/organization/configuration/constants/constants.service';
import {OrganizationConstantValue} from '@models/adminstration/organization/constants/organization-constant-value';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {UtilsService} from '@services/utils/utils.service';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {ChangePasswordComponent} from '@components/user/edit-profile/modal/change-password/change-password.component';
import {EnvironmentConfig} from '@environments/environment';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {Role} from '@models/user/role';
import {ModalService} from '@services/modal/modal.service';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';

@Component({
    selector: 'scitis-edit-profile',
    templateUrl: './edit-profile.component.html',
    styleUrls: ['./edit-profile.component.scss'],
    standalone: false
})
export class EditProfileComponent {

  public user: User | undefined = undefined;
  public organizationConstants: OrganizationConstant[] | undefined;
  public maxNotificationRetention = 0;
  public notificationRetentionForm: UntypedFormGroup = this.formBuilder.group({});
  public valueTooBigMessage = '';
  public saveButtonIsDisabled = true;
  public isLoadingConstants = true;
  public managePasswordTooltip = '';
  public isViewerRole: boolean = false;

  private possibleOrganizations: Organization[] = new Array<Organization>();
  private updateNotificationRetentionSuccessMessage = '';
  private updateNotificationRetentionErrorMessage = '';
  private changePasswordSuccessMessage = '';
  private changePasswordErrorMessage = '';
  private CURRENT_PAGE_NAME = 'EditProfileComponent';

  constructor(
      private modalService: ModalService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private elasticSearchService: ElasticSearchService,
      private constantsService: ConstantsService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private utilsService: UtilsService,
      private formBuilder: UntypedFormBuilder,
      private translate: TranslateService,
      private notification: NotificationService,
      private loadingIndicator: LoadingIndicatorService) {
    this.setMessages();
    this.generateNotificationRetentionForm();
    this.setAuthenticatedUserRoleAndOrganization();
    this.setMaxNotificationRetentionObservable();
  }

  get formControls() {
    return this.notificationRetentionForm.controls;
  }

  get maxExceededMessage(): string {
    return this.valueTooBigMessage + ' ' + this.maxNotificationRetention;
  }

  get showChangePassword(): boolean {
    return !EnvironmentConfig.PortalSettings.ssoEnabled;
  }

  get ssoProfileLink(): string {
    return EnvironmentConfig.PortalSettings.ssoProfileLink;
  }

  get managePassword(): boolean {
    return EnvironmentConfig.PortalSettings.ssoEnabled;
  }

  getOrganization(): string {
    if (this.user) {
      const filteredOrganizations = this.possibleOrganizations.filter(m => m.id === this.user?.parentId);
      if (filteredOrganizations && filteredOrganizations.length > 0) {
        return filteredOrganizations[0].name;
      }
    }
    return '';
  }

  getOrderedConstantValues(constantValues: OrganizationConstantValue[]) {
    return this.tableHeaderSortableService.sortItems<OrganizationConstantValue>(constantValues, 'startTimestamp', 'asc');
  }

  parseSignalValue(type, value, precision) {
    return this.utilsService.parseSignalValue(type, value, precision);
  }

  getValueAnUnit(constant: OrganizationConstant, constantValue: OrganizationConstantValue): string {
    return this.parseSignalValue(constant.portalSetting.value, constantValue.value, constant.tenantConstant.decimalPlaces) + constant.tenantConstant.unit;
  }

  notificationRetentionDaysChanged(): void {
    if (this.user?.notificationRetentionDays !== this.notificationRetentionForm.controls.notificationRetentionDays.value) {
      this.saveButtonIsDisabled = false;
    }
    if (this.formControls.notificationRetentionDays.errors) {
      this.saveButtonIsDisabled = true;
    }
  }

  saveNotificationRetention(): void {
    if (this.notificationRetentionForm.valid) {
      this.loadingIndicator.show();
      this.firebaseAuthenticationService.setNotificationRetention(this.notificationRetentionForm.controls.notificationRetentionDays.value).subscribe(() => {
        this.notification.success(this.updateNotificationRetentionSuccessMessage, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicator.hide();
      }, error => {
        console.log(error);
        this.notification.error(this.updateNotificationRetentionErrorMessage, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicator.hide();
      });
    }
  }

  openChangePasswordModal(): void {
    const modalRef = this.modalService.open(ChangePasswordComponent, {size: 'md'});
    modalRef.result.then((newPassword: string) => {
      if (this.user) {
        this.user.credentialHash = newPassword;
        this.changePassword();
      }
    });
  }

  changePassword(): void {
    if (!this.user || !this.user.credentialHash || this.user.credentialHash.isNullOrWhitespace()) {
      return;
    }
    this.loadingIndicator.show();
    this.firebaseAuthenticationService.changePassword(this.user).subscribe(() => {
      this.notification.success(this.changePasswordSuccessMessage, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicator.hide();
    }, error => {
      console.log(error);
      this.notification.error(this.changePasswordErrorMessage, error, this.CURRENT_PAGE_NAME, true);
      this.loadingIndicator.hide();
    });

  }

  getConstantValueClass(value: OrganizationConstantValue): string {
    if (this.constantIntervalIsActive(value)) {
      return 'font-weight-bold text-success';
    }
    return '';
  }

  private constantIntervalIsActive(value: OrganizationConstantValue): boolean {
    const current = new Date().getTime();
    const start = value.startTimestamp;
    const end = value.endTimestamp;

    return start <= current && end >= current;
  }

  private setAuthenticatedUserRoleAndOrganization() {
    this.firebaseAuthenticationService.getLoggedUserDetails().subscribe((user: User) => {
      this.user = user;
      const userRole = new Role(user);
      this.isViewerRole = userRole.isViewerRole;
      this.notificationRetentionForm.controls.notificationRetentionDays.setValue(this.user?.notificationRetentionDays);
      this.setPossibleOrganizations(user, userRole);
      this.setOrganizationConstants();
    }, (error: HttpResponse<any>) => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
    });
  }

  private setPossibleOrganizations(user: User, role: Role): void {
    const rootOrganizationId = role.isSuperAdminRole ? -1 : user.parentId;
    this.elasticSearchService.listAllOrganizations(rootOrganizationId)
    .subscribe({
      next: (result: ElasticSearchResponseEntity) => {
        if (result.result.data) {
          this.possibleOrganizations = [...result.result.data];
        }
      },
      error: err => {
        this.possibleOrganizations = [];
      }
    });
  }

  private setOrganizationConstants(): void {
    if (this.user) {
      this.constantsService.listConstants(this.user?.parentId).subscribe((success) => {
        this.organizationConstants = success;
        this.isLoadingConstants = false;
      }, (error) => {
        console.error('Error loading organization constants', JSON.stringify(error));
        this.organizationConstants = new Array<OrganizationConstant>();
      });
    }
  }

  private generateNotificationRetentionForm(): void {
    this.notificationRetentionForm = this.formBuilder.group({
      notificationRetentionDays: new UntypedFormControl({value: 1})
    });
  }

  private setMaxNotificationRetentionObservable(): void {
    this.firebaseAuthenticationService.getMaxNotificationRetention().subscribe((response: number[]) => {
      if (response && response.length > 0) {
        this.maxNotificationRetention = response[0];
        this.notificationRetentionForm.controls.notificationRetentionDays.setValidators([Validators.required, Validators.min(1), Validators.max(this.maxNotificationRetention)]);
      } else {
        this.notificationRetentionForm.controls.notificationRetentionDays.setValidators([Validators.required, Validators.min(1)]);
      }
    }, error => {
      console.log(error);
    });
  }

  private setMessages(): void {
    this.valueTooBigMessage = this.translate.instant('Exceeds maximum of:');
    this.updateNotificationRetentionSuccessMessage = this.translate.instant('The notification retention was saved successfully!');
    this.updateNotificationRetentionErrorMessage = this.translate.instant('The notification retention could not be saved because it\'s value is higher than the allowed portal maximum.');
    this.changePasswordSuccessMessage = this.translate.instant('Password was changed successfully!');
    this.changePasswordErrorMessage = this.translate.instant('Password change failed.');
    this.managePasswordTooltip = this.translate.instant('Will open up the identity provider page in a new tab.');
  }

}
